'use client';

import { useEffect, useState } from 'react';
import useSWR from 'swr';
import styled from 'styled-components';

import { AccordionPage } from './AccordionPage';
import type { InfoMenuItem } from './config';
import { getInfoDocsDirectory } from './get-info-docs-directory';

const fetcher = (url: string) =>
  fetch(url).then((res) => {
    if (!res.ok) {
      throw new Error(`Failed to fetch ${url}. Status: ${res.status}`);
    }

    return res.text();
  });

type InfoDocumentClientProps = {
  className?: string;
  initialHtml?: string | null;
  item?: InfoMenuItem;
  locale: string;
};

export const InfoDocumentClient = ({
  className,
  initialHtml,
  item,
  locale,
}: InfoDocumentClientProps) => {
  const [isReady, setIsReady] = useState(false);
  const [lastData, setLastData] = useState(initialHtml ?? '');
  const documentUrl = item
    ? `/docs/${getInfoDocsDirectory(locale)}/${item.value}.html`
    : null;
  const { data, isLoading } = useSWR(documentUrl, fetcher, {
    fallbackData: initialHtml ?? undefined,
  });

  useEffect(() => {
    if (data) {
      setLastData(data);
    }
  }, [data]);

  useEffect(() => {
    setLastData(initialHtml ?? '');
  }, [initialHtml, item?.id]);

  useEffect(() => {
    setIsReady(true);
  }, []);

  const html = data ?? lastData;

  if (isLoading && !html) {
    return null;
  }

  return (
    <div className={className} data-ready={isReady}>
      {html && <AccordionPage html={html} isExpanded={item?.id === 'terms'} />}
      {isLoading && <LoadingShadow />}
    </div>
  );
};

const LoadingShadow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: #000000;
  opacity: 0.5;
`;
